import { logSwallowed } from '@/lib/errors';
import { getSDK } from '@/store/sdkStore';
import { localCache } from '@/lib/localCache';
import { createLogger } from '@/lib/logger';
import { hasAuthToken } from '@/lib/eventLog';
import { isTransportError } from '@/lib/errors';
import { markConnectionOffline, useConnectionStore } from '@/lib/connection';
import {
  cacheMerge,
  cacheRemove,
  queueAdd,
  queueGetPending,
  queueGetFailed,
  queueUpdate,
  queueRemove,
  queueClear,
  metaGet,
  metaSet,
  metaSetVersion,
  metaSetStatus,
  baseStoreGet,
  type SyncOperation,
  type SyncQueueItem,
  type Row,
} from '@/lib/storage';
import type { SyncConflict } from '@zdc/sdk';

const log = createLogger('SyncEngine');

// ЛЮБАЯ ошибка синхронизации → журнал регистрации (категория sync, level error,
// детали — техническая причина). SSoT журнала — eventLog (онлайн→сервер,
// офлайн→очередь). Без этого «sync error» в StatusBar не видна в журнале.
function logSyncError(phase: string, baseId: string, err: unknown): void {
  try {
    const detail = err instanceof Error
      ? `${err.message}${err.stack ? '\n' + err.stack.slice(0, 1000) : ''}`
      : String(err);
    void import('@/lib/eventLog').then((m) => {
      m.eventLog.error('sync', `Синхронизация (${phase}): ${err instanceof Error ? err.message : String(err)}`, {
        entityCode: baseId,
        details: detail,
      }).catch((e) => logSwallowed(e, 'lib/syncEngine.ts:41'));
    });
  } catch { /* журнал недоступен — не критично */ }
}

const MAX_RETRIES = 5;
const RETRY_DELAYS_MS = [1000, 5000, 25000, 125000, 625000];

// Jitter в ретраях (2026-08-30, консультант): при МАССОВОМ восстановлении
// сети все клиенты синкаются одновременно («thundering herd» → сервер
// захлёбывается). Добавляем случайную задержку до +30% от базовой —
// запросы расходятся, сервер ровно разгружается. Детерминированность не
// страдает: сам ретрай/очередь не зависят от точной задержки.
function getRetryDelay(attempt: number): number {
  const base = RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)];
  return base + base * 0.3 * Math.random();
}

let syncRunning = false;
let currentBaseId: string | null = null;

const statusListeners = new Set<(status: { status: string; error?: string }) => void>();

function notifyStatus(status: { status: string; error?: string }) {
  for (const listener of statusListeners) {
    try {
      listener(status);
    } catch (err) {
      log.error('Status listener error:', err);
    }
  }
}

function generateId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

async function resolveBaseId(baseId?: string): Promise<string> {
  if (baseId) return baseId;
  if (currentBaseId) return currentBaseId;
  const base = await baseStoreGet();
  if (base?.id) {
    currentBaseId = base.id;
    return base.id;
  }
  return '';
}

export async function setCurrentBaseId(baseId: string): Promise<void> {
  currentBaseId = baseId;
}

export async function addToQueue(
  tableName: string,
  recordId: string,
  operation: SyncOperation,
  data: Record<string, unknown>,
  expectedVersion: number = 0,
  formAttributes?: Record<string, string>,
  baseId?: string
): Promise<SyncQueueItem> {
  const resolvedBaseId = await resolveBaseId(baseId);
  if (!resolvedBaseId) {
    throw new Error('Cannot add to queue: no base selected');
  }

  log.info(`Queue ${operation} ${tableName}/${recordId} for base ${resolvedBaseId}`);
  const item = await queueAdd(resolvedBaseId, {
    table_name: tableName,
    record_id: recordId,
    data,
    operation,
    expected_version: expectedVersion,
    // Реквизиты ФОРМЫ (виртуальные): едут в офлайн-операции — onBeforeSave
    // выполнится на СЕРВЕРЕ при синке с теми же РеквизитыФормы.X
    ...(formAttributes && Object.keys(formAttributes).length > 0 ? { form_attributes: formAttributes } : {}),
  });

  // Register background sync so the queue is sent when connectivity returns
  registerBackgroundSync(resolvedBaseId).catch((e) => logSwallowed(e, 'lib/syncEngine.ts:123'));

  // Trigger sync attempt if online
  scheduleSync(resolvedBaseId);
  return item;
}

async function registerBackgroundSync(baseId: string): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  const sw = navigator.serviceWorker as any;
  if (!('sync' in sw)) return;

  try {
    const registration = await sw.ready;
    if (registration && 'sync' in registration) {
      await (registration as any).sync.register('hydra-sync');
      log.info('Background sync registered for base', baseId);
    }
  } catch (err) {
    log.warn('Background sync registration failed:', err);
  }
}

// Предохранитель пагинации pull: даже при ошибке сервера цикл НЕ может
// крутиться бесконечно — по достижении потолка синхронизация останавливается
// с явной ошибкой (в журнал), а не молча.
const MAX_PULL_ITERATIONS = 100000;

// Причина остановки pull при отсутствии прогресса (позиция не двигается /
// пустые пачки) — единая формулировка для журнала и статуса.
function pullStallError(baseId: string, detail: string): Error {
  return new Error(
    `Синхронизация (pull) остановлена: нет прогресса для базы ${baseId} — ${detail}`
  );
}

async function pull(baseId: string): Promise<void> {
  if (!baseId) return;

  const meta = await metaGet(baseId);
  let lastVersion = meta?.last_sync_version ?? 0;

  try {
    notifyStatus({ status: 'syncing' });
    const sdk = getSDK();

    // ЦИКЛ вместо рекурсии: при hasMore=true и больших объёмах (>1000 записей)
    // рекурсивный вызов pull() сам себя переполнял стек
    // («Maximum call stack size exceeded»). Итеративный while с КУРСОРОМ:
    // сервер возвращает next_cursor_table/next_cursor_offset, клиент передаёт
    // их в следующий запрос — каждая пачка идёт ДАЛЬШЕ, а не с начала.
    let totalPulled = 0;
    let cursorTable = '';
    let cursorOffset = 0;
    let iterations = 0;
    // Сторож: позиция (версия+курсор) не изменилась дважды подряд → цикл.
    let unchangedPosition = 0;
    let prevPosition = `${lastVersion}|${cursorTable}|${cursorOffset}`;
    let hasMore = true;

    while (hasMore) {
      iterations += 1;
      if (iterations > MAX_PULL_ITERATIONS) {
        throw pullStallError(baseId, `превышен лимит итераций (${MAX_PULL_ITERATIONS})`);
      }

      const response = await sdk.base.pullChanges({
        baseId,
        lastVersion: BigInt(lastVersion),
        limit: 1000,
        cursorTable,
        cursorOffset,
      });

      const records = response.records || [];
      totalPulled += records.length;

      // Group by table for merge
      const byTable = new Map<string, Row[]>();
      const deletesByTable = new Map<string, string[]>();

      for (const record of records) {
        const table = record.table;
        const rowId = record.rowId;

        if (record.operation === 'delete') {
          const list = deletesByTable.get(table) || [];
          list.push(rowId);
          deletesByTable.set(table, list);
          continue;
        }

        const row: Row = { id: rowId };
        if (record.data) {
          for (const [k, v] of Object.entries(record.data)) {
            row[k] = v;
          }
        }

        const list = byTable.get(table) || [];
        list.push(row);
        byTable.set(table, list);
      }

      for (const [table, rows] of byTable.entries()) {
        await cacheMerge('public', table, rows);
      }

      for (const [table, ids] of deletesByTable.entries()) {
        for (const id of ids) {
          await cacheRemove('public', table, id);
        }
      }

      const hasMoreFlag = Boolean(response.hasMore);
      const newVersion = Number(response.baseVersion) || lastVersion;
      const nextTable = response.nextCursorTable || '';
      const nextOffset = Number(response.nextCursorOffset) || 0;

      // Прогресс: либо новая версия, либо сдвинулся курсор, либо пришли записи.
      const positionChanged =
        newVersion !== lastVersion || nextTable !== cursorTable || nextOffset !== cursorOffset;
      const position = `${newVersion}|${nextTable}|${nextOffset}`;

      if (hasMoreFlag && records.length === 0 && !positionChanged) {
        // Сервер сообщил hasMore, но не отдал ни строки и не сдвинул позицию —
        // это ЗАВЕДОМАЯ петля (та же первая пачка). Останавливаемся явно.
        throw pullStallError(baseId, `пустая пачка без сдвига позиции (версия ${newVersion})`);
      }
      if (hasMoreFlag && position === prevPosition) {
        unchangedPosition += 1;
        if (unchangedPosition >= 2) {
          throw pullStallError(baseId, `позиция не меняется повторно (${position})`);
        }
      } else {
        unchangedPosition = 0;
      }
      prevPosition = position;

      lastVersion = newVersion > lastVersion ? newVersion : lastVersion;
      cursorTable = nextTable;
      cursorOffset = nextOffset;
      hasMore = hasMoreFlag;

      log.info(
        `Pulled ${records.length} records for base ${baseId}, version ${lastVersion}, cursor ${cursorTable || '-'}@${cursorOffset} (total ${totalPulled})`
      );
      // hasMore → тянем СЛЕДУЮЩУЮ порцию с курсором (без задержки).
    }

    await metaSetVersion(baseId, lastVersion);
    await metaSetStatus(baseId, 'idle');
  } catch (err) {
    // ТРАНСПОРТНЫЙ сбой (связь с сервером потеряна) — это НЕ ошибка
    // синхронизации: офлайн-режим, очередь ждёт восстановления связи
    // (StatusBar: «Офлайн · N в очереди»; повтор — при подтверждённом online).
    if (isTransportError(err)) {
      markConnectionOffline();
      await metaSetStatus(baseId, 'idle');
      log.info(`pull paused: связь с сервером потеряна (${String(err)})`);
      throw err;
    }
    logSyncError('pull', baseId, err);
    await metaSetStatus(baseId, 'error', String(err));
    notifyStatus({ status: 'error', error: String(err) });
    throw err;
  }
}

async function push(baseId: string): Promise<void> {
  if (!baseId) return;

  const queue = await queueGetPending(baseId);
  if (queue.length === 0) {
    await metaSetStatus(baseId, 'idle');
    return;
  }

  try {
    notifyStatus({ status: 'syncing' });

    // Mark all as syncing
    for (const item of queue) {
      await queueUpdate(baseId, item.id, { status: 'syncing' });
    }

    const sdk = getSDK();

    // save_form (единое сохранение кастомной формы): обрабатываются ОТДЕЛЬНО —
    // это НЕ ChangeRecord (sync-сервис их не понимает), а RPC MetadataService.SaveForm.
    // Каждый элемент — в СВОЁМ try (ошибка не валит PushChanges ниже): успех → убрать
    // из очереди; ошибка → retries+1, при MAX_RETRIES → DLQ (status 'failed').
    const formItems = queue.filter((it) => (it as any).operation === 'save_form');
    for (const item of formItems) {
      const meta = (item as any).data || {};
      const formId = String(meta.formId || '');
      const recordId = String(meta.recordId || '');
      const values = (meta.values || {}) as Record<string, string>;
      try {
        if (!formId) throw new Error('save_form: formId пуст');
        await sdk.metadata.saveForm({ formId, recordId, values } as any);
        await queueRemove(baseId, item.id);
      } catch (err) {
        const retries = item.retries + 1;
        const lastError = String((err as any)?.message || err);
        if (retries >= MAX_RETRIES) {
          await queueUpdate(baseId, item.id, { status: 'failed', retries, last_error: lastError });
        } else {
          await queueUpdate(baseId, item.id, { status: 'pending', retries, last_error: lastError });
        }
        logSyncError('save_form', baseId, err);
      }
    }

    const records = queue
      .filter((it) => (it as any).operation !== 'save_form')
      .map(item => ({
      table: item.table_name,
      rowId: item.record_id,
      operation: item.operation,
      data: Object.fromEntries(
        Object.entries(item.data).map(([k, v]) => [k, String(v ?? '')])
      ) as { [key: string]: string },
      expectedVersion: BigInt(item.expected_version || 0),
      // Реквизиты формы (виртуальные) из offline-операции — доходят до
      // onBeforeSave на сервере (РеквизитыФормы.X)
      ...((item as any).form_attributes && Object.keys((item as any).form_attributes).length > 0
        ? { formAttributes: (item as any).form_attributes }
        : {}),
    }));

    const response = await sdk.base.pushChanges({
      baseId,
      records,
    });

    const accepted = Number(response.accepted) || 0;
    const rejected = Number(response.rejected) || 0;
    const conflicts = (response.conflicts || []) as SyncConflict[];
    const mappings = response.mapping || [];

    // Build conflict lookup by table:recordId
    const conflictByKey = new Map<string, SyncConflict>();
    for (const c of conflicts) {
      conflictByKey.set(`${c.table}:${c.recordId}`, c);
    }
    // Build ID mapping lookup by table:localId
    const mappingByKey = new Map<string, string>();
    for (const m of mappings) {
      if (m.localId && m.serverId) {
        mappingByKey.set(`${m.tableName}:${m.localId}`, m.serverId);
      }
    }

    let acceptedRemaining = accepted;
    for (const item of queue) {
      const key = `${item.table_name}:${item.record_id}`;
      const conflict = conflictByKey.get(key);
      if (conflict) {
        await queueUpdate(baseId, item.id, {
          status: 'failed',
          last_error: `conflict (server v${conflict.serverVersion}): local record rejected`,
        });
        continue;
      }
      const serverId = mappingByKey.get(key);
      if (serverId) {
        // Server accepted with a different ID — update our queue reference
        await queueUpdate(baseId, item.id, { record_id: serverId });
        // И применяем маппинг к кэшу: иначе ссылки на офлайн-созданную запись
        // (созданы с клиентским UUID) не резолвятся в представления (UUID вместо имени).
        try {
          const dot = item.table_name.indexOf('.');
          const sch = dot >= 0 ? item.table_name.slice(0, dot) : 'public';
          const tbl = dot >= 0 ? item.table_name.slice(dot + 1) : item.table_name;
          localCache.update(sch, tbl, item.record_id, { id: serverId } as Partial<Row>);
        } catch {
          // ignore cache update errors
        }
      }
      if (acceptedRemaining > 0) {
        await queueRemove(baseId, item.id);
        acceptedRemaining--;
      } else {
        await queueUpdate(baseId, item.id, {
          status: 'failed',
          last_error: 'rejected by server or conflict',
        });
      }
    }

    const newVersion = Number(response.baseVersion) || 0;
    const meta = await metaGet(baseId);
    if (newVersion > (meta?.last_sync_version ?? 0)) {
      await metaSetVersion(baseId, newVersion);
    }

    if (rejected > 0) {
      await metaSetStatus(baseId, 'conflict', `${rejected} rejected`);
      notifyStatus({ status: 'conflict', error: `${rejected} rejected` });
    } else {
      await metaSetStatus(baseId, 'idle');
    }

    log.info(`Pushed base ${baseId}: ${accepted} accepted, ${rejected} rejected`);
  } catch (err) {
    // ТРАНСПОРТНЫЙ сбой — офлайн-режим: НЕ считаем попытки (иначе офлайн-
    // накопления уезжали бы в DLQ) и НЕ пишем «ошибку» — очередь дождётся
    // восстановления связи и отправится (попытки — только для серверных ошибок).
    if (isTransportError(err)) {
      markConnectionOffline();
      await metaSetStatus(baseId, 'idle');
      log.info(`push paused: связь с сервером потеряна (${String(err)})`);
      throw err;
    }
    // Increment retries and re-queue or DLQ
    const failed = await queueGetPending(baseId);
    for (const item of failed) {
      const retries = item.retries + 1;
      if (retries >= MAX_RETRIES) {
        await queueUpdate(baseId, item.id, {
          status: 'failed',
          retries,
          last_error: String(err),
        });
      } else {
        await queueUpdate(baseId, item.id, {
          status: 'pending',
          retries,
          last_error: String(err),
        });
      }
    }

    await metaSetStatus(baseId, 'error', String(err));
    notifyStatus({ status: 'error', error: String(err) });
    logSyncError('push', baseId, err);

    // ПРОАКТИВНЫЙ ретрай с JITTER (2026-08-30, консультант): при сбое
    // планируем повторный syncAll с задержкой base(+0..30% случайных) —
    // при массовом восстановлении сети клиенты синкаются не в один миг
    // (thundering herd), а разнесённо. Задержка по попытке — из
    // RETRY_DELAYS_MS (1с→5с→25с→125с→625с).
    void queueGetPending(baseId).then((q) => {
      const attempt = q.reduce((m, i) => Math.max(m, i.retries), 0);
      const delay = getRetryDelay(attempt);
      setTimeout(() => scheduleSync(baseId), delay);
    }).catch((e) => { logSwallowed(e, 'lib/syncEngine.ts:470'); return undefined; });
    throw err;
  }
}

export async function syncAll(baseId?: string): Promise<void> {
  // Без активной сессии синхронизировать нечего (иначе 401-шум на лаунчере/
  // до входа: «session revoked or expired» каждые 60с).
  if (!hasAuthToken()) return;

  // Режим офлайн (связь с сервером потеряна): синхронизация не запускается —
  // очередь спокойно ждёт ПОДТВЕРЖДЁННОГО online (живой стрим EventBus или
  // event 'online' браузера будят scheduleSync). Без этого каждый провал
  // писал «ошибку синхронизации» и засорял журнал.
  if (useConnectionStore.getState().status === 'offline') {
    log.info('sync skipped: режим офлайн (связь с сервером потеряна)');
    return;
  }

  const resolvedBaseId = await resolveBaseId(baseId);
  if (!resolvedBaseId) {
    log.warn('syncAll called without baseId');
    return;
  }

  if (syncRunning) return;
  syncRunning = true;

  try {
    await pull(resolvedBaseId);
    await push(resolvedBaseId);
  } finally {
    syncRunning = false;
  }
}

let scheduledSyncTimer: ReturnType<typeof setTimeout> | null = null;

export function scheduleSync(baseId?: string): void {
  if (scheduledSyncTimer) return;
  scheduledSyncTimer = setTimeout(() => {
    scheduledSyncTimer = null;
    syncAll(baseId).catch(err => {
      log.error('Scheduled sync failed:', err);
    });
  }, 500);
}

export function cancelScheduledSync(): void {
  if (scheduledSyncTimer) {
    clearTimeout(scheduledSyncTimer);
    scheduledSyncTimer = null;
  }
}

export async function getDeadLetter(baseId?: string): Promise<SyncQueueItem[]> {
  const resolvedBaseId = await resolveBaseId(baseId);
  if (!resolvedBaseId) return [];
  return queueGetFailed(resolvedBaseId);
}

export async function retryDeadLetter(
  id: string,
  baseId?: string
): Promise<void> {
  const resolvedBaseId = await resolveBaseId(baseId);
  if (!resolvedBaseId) return;

  const failed = await queueGetFailed(resolvedBaseId);
  const item = failed.find(i => i.id === id);
  if (!item) return;

  await queueRemove(resolvedBaseId, id);
  await queueAdd(resolvedBaseId, {
    table_name: item.table_name,
    record_id: item.record_id,
    data: item.data,
    operation: item.operation,
    expected_version: item.expected_version,
  });
  await scheduleSync(resolvedBaseId);
}

export async function clearQueue(baseId?: string): Promise<void> {
  const resolvedBaseId = await resolveBaseId(baseId);
  if (!resolvedBaseId) return;
  await queueClear(resolvedBaseId);
}

export function onStatusChange(
  callback: (status: { status: string; error?: string }) => void
): () => void {
  statusListeners.add(callback);
  return () => statusListeners.delete(callback);
}

export function isRunning(): boolean {
  return syncRunning;
}

export async function getLastVersion(baseId?: string): Promise<number> {
  const resolvedBaseId = await resolveBaseId(baseId);
  if (!resolvedBaseId) return 0;
  const meta = await metaGet(resolvedBaseId);
  return meta?.last_sync_version ?? 0;
}

export async function getStatus(baseId?: string): Promise<{ status: string; error?: string }> {
  const resolvedBaseId = await resolveBaseId(baseId);
  if (!resolvedBaseId) return { status: 'idle' };
  const meta = await metaGet(resolvedBaseId);
  return { status: meta?.status ?? 'idle', error: meta?.error };
}

export async function getPendingCount(baseId?: string): Promise<number> {
  const resolvedBaseId = await resolveBaseId(baseId);
  if (!resolvedBaseId) return 0;
  const pending = await queueGetPending(resolvedBaseId);
  return pending.length;
}

export const syncEngine = {
  addToQueue,
  syncAll,
  pull,
  push,
  scheduleSync,
  cancelScheduledSync,
  getDeadLetter,
  retryDeadLetter,
  clearQueue,
  onStatusChange,
  isRunning,
  getLastVersion,
  getStatus,
  getPendingCount,
  setCurrentBaseId,
};
